import { useEffect, useState } from "react";
import { ExportProgress } from "../DataSet/DatasetProvider";
import "./ExportProgressBar.scss";

interface ExportProgressBarProps {
  progress: ExportProgress;
  onCancel?: () => void;
  onDismissError?: () => void;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) return `${mins}m ${secs}s`;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return `${hours}h ${remainingMins}m ${secs}s`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ExportProgressBar({ progress, onCancel, onDismissError }: ExportProgressBarProps) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  useEffect(() => {
    if (!progress.isExporting || !progress.startTime) {
      setElapsedSeconds(0);
      setShowConfirmCancel(false);
      return;
    }

    const updateElapsed = () => {
      const elapsed = Math.floor((Date.now() - progress.startTime!) / 1000);
      setElapsedSeconds(elapsed);
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);

    return () => clearInterval(interval);
  }, [progress.isExporting, progress.startTime]);

  if (!progress.isExporting && !progress.exportError) return null;

  if (progress.exportError) {
    return (
      <div className="export-progress-overlay">
        <div className="export-progress-modal">
          <div className="export-progress-header">
            <span className="export-progress-title">Export failed</span>
          </div>
          <div className="export-progress-error-message">
            {progress.exportError}
          </div>
          <div className="export-progress-actions">
            <button className="export-progress-cancel" onClick={onDismissError}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isPreparing = progress.totalRows === 0;

  return (
    <div className="export-progress-overlay">
      <div className="export-progress-modal">
        <div className="export-progress-header">
          <span className="export-progress-title">
            {isPreparing ? "Preparing export..." : "Exporting data..."}
          </span>
        </div>

        {isPreparing ? (
          <div className="export-progress-preparing">
            <span>Fetching data information, please wait...</span>
          </div>
        ) : (
          <div className="export-progress-stats">
            <span className="export-progress-stat">
              <strong>{progress.downloadedRows.toLocaleString()}</strong> of{" "}
              <strong>{progress.totalRows.toLocaleString()}</strong> rows
            </span>
            <span className="export-progress-stat">
              <strong>{formatBytes(progress.downloadedBytes)}</strong> downloaded
            </span>
          </div>
        )}

        <div className="export-progress-bar-container">
          <div
            className={`export-progress-bar-fill ${isPreparing ? "indeterminate" : ""}`}
            style={{ width: isPreparing ? "100%" : `${progress.percentage}%` }}
          />
        </div>

        <div className="export-progress-footer">
          {isPreparing ? (
            <span className="export-progress-elapsed">
              Elapsed: {formatDuration(elapsedSeconds)}
            </span>
          ) : (
            <>
              <span className="export-progress-percentage">
                {progress.percentage}%
              </span>
              <span className="export-progress-elapsed">
                Elapsed: {formatDuration(elapsedSeconds)}
              </span>
              {progress.estimatedTimeRemaining !== null &&
                progress.estimatedTimeRemaining > 0 && (
                  <span className="export-progress-remaining">
                    ~{formatDuration(progress.estimatedTimeRemaining)} remaining
                  </span>
                )}
            </>
          )}
        </div>

        {onCancel && (
          <div className="export-progress-actions">
            {showConfirmCancel ? (
              <div className="export-progress-confirm">
                <span className="export-progress-confirm-message">
                  Cancel export? All progress will be lost.
                </span>
                <div className="export-progress-confirm-buttons">
                  <button
                    className="export-progress-confirm-no"
                    onClick={() => setShowConfirmCancel(false)}
                  >
                    Continue
                  </button>
                  <button
                    className="export-progress-cancel"
                    onClick={onCancel}
                  >
                    Yes, cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="export-progress-cancel"
                onClick={() => setShowConfirmCancel(true)}
              >
                Cancel
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
